import { Controller } from "@hotwired/stimulus"
import toast from "../services/toast"

export default class extends Controller {
  static targets = [
    "toolbar", "entriesBtn", "exitsBtn", "qrBtn", 
    "entriesPanel", "exitsPanel", "qrPanel", "advancedPanel",
    "advancedSearchBtn", "advancedSearchPanel",
    "templateSelect", "selectedTemplateId", "templatePreview",
    "previewCanvas", "previewName", "previewSize",
    "checkboxAll", "checkbox", "selectedCount", "generateButton",
    "filterBadge", "searchForm",
    "entriesNotes", "entriesNotesCounter",
    "exitsNotes", "exitsNotesCounter",
    "entriesTableContainer", "exitsTableContainer",
    "entriesTotal", "exitsTotal",
    "submitEntriesButton", "submitExitsButton"
  ]

  connect() {
    // Estado de selecciones
    this.selectedIds = new Set()
    
    // Panel activo actual
    this.activePanel = null
    
    // Ocultar todos los paneles al inicio
    this.hideAllPanels()
    
    // Seleccionar plantilla predeterminada automáticamente
    this.selectDefaultTemplate()
    
    // Listeners para frame loads
    this._onFrameLoad = (event) => {
      if (event.target && event.target.id === 'inventories_list') {
        // Reset selections after search
        this.selectedIds.clear()
        if (this.hasCheckboxAllTarget) this.checkboxAllTarget.checked = false
        this.updateSelectedCount()
      }
    }
    
    document.addEventListener('turbo:frame-load', this._onFrameLoad)
  }

  disconnect() {
    document.removeEventListener('turbo:frame-load', this._onFrameLoad)
  }

  // Método principal para toggle de herramientas
  toggleTool(event) {
    event.preventDefault()
    const tool = event.currentTarget.dataset.tool
    
    if (this.activePanel === tool) {
      // Si el panel ya está activo, lo ocultamos
      this.hideAllPanels()
      this.activePanel = null
      this.removeActiveButton()
    } else {
      // Ocultar todos los paneles y mostrar el seleccionado
      this.hideAllPanels()
      this.showPanel(tool)
      this.activePanel = tool
      this.setActiveButton(event.currentTarget)
    }
  }

  hideAllPanels() {
    if (this.hasEntriesPanelTarget) this.entriesPanelTarget.classList.remove("is-open")
    if (this.hasExitsPanelTarget) this.exitsPanelTarget.classList.remove("is-open")
    if (this.hasQrPanelTarget) this.qrPanelTarget.classList.remove("is-open")
    if (this.hasAdvancedPanelTarget) {
      this.advancedPanelTarget.classList.remove("is-open")
      this.advancedPanelTarget.style.display = "none"
    }
  }

  showPanel(panelName) {
    switch(panelName) {
      case "entries":
        if (this.hasEntriesPanelTarget) {
          this.entriesPanelTarget.classList.add("is-open")
          this.populateEntriesTable()
        }
        break
      case "exits":
        if (this.hasExitsPanelTarget) {
          this.exitsPanelTarget.classList.add("is-open")
          this.populateExitsTable()
        }
        break
      case "qr":
        if (this.hasQrPanelTarget) this.qrPanelTarget.classList.add("is-open")
        break
      case "advanced":
        if (this.hasAdvancedPanelTarget) {
          this.advancedPanelTarget.classList.add("is-open")
          this.advancedPanelTarget.style.display = "block"
        }
        break
    }
  }

  setActiveButton(button) {
    // Remover active de todos los botones del toolbar
    this.removeActiveButton()
    // Agregar active al botón clickeado
    button.classList.add("active")
  }

  removeActiveButton() {
    const buttons = this.element.querySelectorAll('.right-actions .btn')
    buttons.forEach(btn => btn.classList.remove("active"))
  }

  toggleAdvancedSearch(event) {
    event.preventDefault()
    
    if (!this.hasAdvancedPanelTarget) return
    
    const panel = this.advancedPanelTarget
    const isOpen = panel.classList.contains("is-open") || panel.style.display === "block"
    
    // Remove active from all buttons
    this.removeActiveButton()
    
    if (isOpen) {
      panel.classList.remove("is-open")
      panel.style.display = "none"
      this.activePanel = null
    } else {
      this.hideAllPanels()
      panel.classList.add("is-open")
      panel.style.display = "block"
      this.activePanel = "advanced"
      if (event.currentTarget) event.currentTarget.classList.add('active')
    }
  }

  // Manejo de selecciones
  selectAll(event) {
    const checked = event.target.checked
    
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = checked
      const id = checkbox.value
      
      if (checked) {
        this.selectedIds.add(id)
      } else {
        this.selectedIds.delete(id)
      }
    })
    
    // También actualizar los checkboxes de grupo de producto
    this.element.querySelectorAll('.product-group-checkbox').forEach(cb => {
      cb.checked = checked
    })

    this.updateSelectedCount()
  }

  toggleCheckbox(event) {
    const checkbox = event.target
    const id = checkbox.value

    if (checkbox.checked) {
      this.selectedIds.add(id)
    } else {
      this.selectedIds.delete(id)
    }

    // Actualizar checkbox "select all"
    if (this.hasCheckboxAllTarget) {
      const allChecked = this.checkboxTargets.every(cb => cb.checked)
      this.checkboxAllTarget.checked = allChecked
    }
    
    // Actualizar checkbox del grupo de producto
    const productId = checkbox.dataset.productId
    if (productId) {
      this.updateProductGroupCheckbox(productId)
    }

    this.updateSelectedCount()
  }

  // Toggle all checkboxes for a product group
  toggleProductGroup(event) {
    const checkbox = event.target
    const productId = checkbox.dataset.productId
    const checked = checkbox.checked
    
    // Encontrar todos los checkboxes de inventario de este producto
    const productCheckboxes = this.checkboxTargets.filter(cb => cb.dataset.productId === productId)
    
    productCheckboxes.forEach(cb => {
      cb.checked = checked
      const id = cb.value
      
      if (checked) {
        this.selectedIds.add(id)
      } else {
        this.selectedIds.delete(id)
      }
    })
    
    // Actualizar checkbox "select all"
    if (this.hasCheckboxAllTarget) {
      const allChecked = this.checkboxTargets.every(cb => cb.checked)
      this.checkboxAllTarget.checked = allChecked
    }
    
    this.updateSelectedCount()
  }

  // Update product group checkbox based on individual checkboxes
  updateProductGroupCheckbox(productId) {
    const productCheckboxes = this.checkboxTargets.filter(cb => cb.dataset.productId === productId)
    const productGroupCheckbox = this.element.querySelector(`.product-group-checkbox[data-product-id="${productId}"]`)
    
    if (productGroupCheckbox && productCheckboxes.length > 0) {
      const allChecked = productCheckboxes.every(cb => cb.checked)
      const someChecked = productCheckboxes.some(cb => cb.checked)
      
      productGroupCheckbox.checked = allChecked
      productGroupCheckbox.indeterminate = someChecked && !allChecked
    }
  }

  updateSelectedCount() {
    const count = this.selectedIds.size
    
    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = `${count} producto${count !== 1 ? 's' : ''} seleccionado${count !== 1 ? 's' : ''}`
    }

    if (this.hasGenerateButtonTarget) {
      // El botón se habilita solo si hay productos seleccionados Y una plantilla seleccionada
      const hasTemplate = this.hasSelectedTemplateIdTarget && this.selectedTemplateIdTarget.value
      this.generateButtonTarget.disabled = count === 0 || !hasTemplate
    }
    
    // Actualizar tablas de entradas/salidas si los paneles están abiertos
    if (this.activePanel === 'entries') {
      this.populateEntriesTable()
    } else if (this.activePanel === 'exits') {
      this.populateExitsTable()
    }
  }

  // Seleccionar automáticamente la plantilla predeterminada
  selectDefaultTemplate() {
    if (!this.hasTemplateSelectTarget) return
    
    // Buscar la opción marcada como predeterminada
    const options = this.templateSelectTarget.options
    for (let i = 0; i < options.length; i++) {
      if (options[i].dataset.isDefault === 'true') {
        this.templateSelectTarget.value = options[i].value
        this.onTemplateChange({ target: this.templateSelectTarget })
        return
      }
    }
  }

  // Manejar cambio de plantilla seleccionada
  onTemplateChange(event) {
    const select = event.target
    const selectedOption = select.options[select.selectedIndex]
    
    if (!selectedOption.value) {
      // No hay plantilla seleccionada
      if (this.hasTemplatePreviewTarget) {
        this.templatePreviewTarget.style.display = 'none'
      }
      if (this.hasSelectedTemplateIdTarget) {
        this.selectedTemplateIdTarget.value = ''
      }
      this.updateSelectedCount()
      return
    }
    
    // Guardar el ID de la plantilla seleccionada
    if (this.hasSelectedTemplateIdTarget) {
      this.selectedTemplateIdTarget.value = selectedOption.value
    }
    
    // Obtener datos de la opción
    const name = selectedOption.dataset.name
    const size = selectedOption.dataset.size
    const width = parseFloat(selectedOption.dataset.width) || 100
    const height = parseFloat(selectedOption.dataset.height) || 150
    let elements = []
    
    try {
      elements = JSON.parse(selectedOption.dataset.elements || '[]')
    } catch (e) {
      console.error('Error parsing elements:', e)
    }
    
    // Mostrar la vista previa
    this.renderTemplatePreview(name, size, width, height, elements)
    this.updateSelectedCount()
  }

  // Renderizar vista previa de la plantilla
  renderTemplatePreview(name, size, widthMm, heightMm, elements) {
    if (!this.hasTemplatePreviewTarget || !this.hasPreviewCanvasTarget) return
    
    // Mostrar el contenedor
    this.templatePreviewTarget.style.display = 'block'
    
    // Actualizar info
    if (this.hasPreviewNameTarget) {
      this.previewNameTarget.textContent = name
    }
    if (this.hasPreviewSizeTarget) {
      this.previewSizeTarget.textContent = size
    }
    
    // Calcular escala para la miniatura
    const maxWidth = 120
    const maxHeight = 80
    const scale = Math.min(maxWidth / widthMm, maxHeight / heightMm)
    
    const canvasWidth = Math.round(widthMm * scale)
    const canvasHeight = Math.round(heightMm * scale)
    
    // Configurar el canvas
    const canvas = this.previewCanvasTarget
    canvas.style.width = `${canvasWidth}px`
    canvas.style.height = `${canvasHeight}px`
    canvas.innerHTML = ''
    
    // Factor de conversión px a mm (los elementos están guardados en px con factor 3.78)
    const pxToMm = 3.78
    
    // Mapeo de textType a español
    const textLabels = {
      "brand": "Marca", "model": "Modelo", "price": "Precio", "color": "Color",
      "size": "Talla", "sku": "SKU", "barcode": "Código", "cut": "Corte",
      "category": "Categoría", "general_category": "Cat. General",
      "description": "Descripción", "custom": "Texto", "supplier": "Proveedor",
      "cost": "Costo", "qr_code": "QR"
    }
    
    // Renderizar cada elemento
    elements.forEach(element => {
      const el = document.createElement('div')
      el.className = `preview-element preview-element-${element.type}`
      
      // Convertir de px a mm, luego aplicar escala
      const x = ((element.x || 0) / pxToMm) * scale
      const y = ((element.y || 0) / pxToMm) * scale
      const w = ((element.width || 50) / pxToMm) * scale
      const h = ((element.height || 50) / pxToMm) * scale
      
      el.style.left = `${x}px`
      el.style.top = `${y}px`
      el.style.width = `${w}px`
      el.style.height = `${h}px`
      
      if (element.type === 'qr') {
        // Renderizar QR simulado
        el.innerHTML = this.renderMiniQr()
      } else {
        // Renderizar texto
        const fontSize = Math.max(((element.fontSize || 14) / pxToMm) * scale * 0.9, 5)
        const displayText = textLabels[element.textType] || element.textType || 'Texto'
        
        el.style.fontSize = `${fontSize}px`
        el.style.fontWeight = element.fontWeight || 'normal'
        el.style.fontStyle = element.fontStyle || 'normal'
        el.textContent = displayText
      }
      
      canvas.appendChild(el)
    })
  }

  // Renderizar QR en miniatura
  renderMiniQr() {
    return `<div style="width: 100%; height: 100%; display: grid; grid-template-columns: repeat(5, 1fr); grid-template-rows: repeat(5, 1fr); gap: 1px; background: white;">
      <div style="background: black;"></div><div style="background: black;"></div><div style="background: white;"></div><div style="background: black;"></div><div style="background: black;"></div>
      <div style="background: black;"></div><div style="background: black;"></div><div style="background: white;"></div><div style="background: black;"></div><div style="background: black;"></div>
      <div style="background: white;"></div><div style="background: white;"></div><div style="background: black;"></div><div style="background: white;"></div><div style="background: white;"></div>
      <div style="background: black;"></div><div style="background: black;"></div><div style="background: white;"></div><div style="background: black;"></div><div style="background: white;"></div>
      <div style="background: black;"></div><div style="background: black;"></div><div style="background: white;"></div><div style="background: white;"></div><div style="background: black;"></div>
    </div>`
  }

  // Generar PDF con template seleccionado
  generateQrPdf(event) {
    event.preventDefault()
    
    if (this.selectedIds.size === 0) {
      toast.warning('Por favor selecciona al menos un producto')
      return
    }
    
    const inventoryIds = Array.from(this.selectedIds)
    const templateId = this.hasSelectedTemplateIdTarget ? this.selectedTemplateIdTarget.value : null
    
    if (!templateId) {
      toast.warning('Por favor selecciona una plantilla de QR')
      return
    }
    
    // Crear formulario para envío
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/admin/inventarios/generate_qr_pdf'
    form.target = '_blank'
    
    // CSRF Token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }
    
    // IDs de inventario
    inventoryIds.forEach(id => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'inventory_ids[]'
      input.value = id
      form.appendChild(input)
    })
    
    // Template ID
    const templateInput = document.createElement('input')
    templateInput.type = 'hidden'
    templateInput.name = 'template_id'
    templateInput.value = templateId
    form.appendChild(templateInput)
    
    // Modo canvas
    const modeInput = document.createElement('input')
    modeInput.type = 'hidden'
    modeInput.name = 'mode'
    modeInput.value = 'canvas'
    form.appendChild(modeInput)
    
    document.body.appendChild(form)
    
    // Mostrar loading
    if (this.hasGenerateButtonTarget) {
      const originalText = this.generateButtonTarget.innerHTML
      this.generateButtonTarget.disabled = true
      this.generateButtonTarget.innerHTML = 'Generando PDF...'
      
      form.submit()
      
      // Restaurar botón
      setTimeout(() => {
        this.generateButtonTarget.disabled = false
        this.generateButtonTarget.innerHTML = originalText
        document.body.removeChild(form)
      }, 2000)
    } else {
      form.submit()
      document.body.removeChild(form)
    }
  }

  // Limpiar filtros de búsqueda avanzada
  clearFilters(event) {
    event.preventDefault()
    
    // Buscar el formulario dentro del panel de búsqueda avanzada
    const form = this.hasSearchFormTarget
      ? this.searchFormTarget
      : this.hasAdvancedPanelTarget 
        ? this.advancedPanelTarget.querySelector('form')
        : this.element.querySelector('.tool-panel form')
    
    if (form) {
      // Limpiar todos los inputs (text y number)
      form.querySelectorAll('input[type="text"], input[type="number"]').forEach(input => {
        const name = input.getAttribute('name')
        // No limpiar sort, direction ni search_term
        if (name !== 'sort' && name !== 'direction' && name !== 'search_term') {
          input.value = ''
        }
      })
      
      // Resetear selects a su valor por defecto o 'all'
      form.querySelectorAll('select').forEach(select => {
        // Check if there's an option with value 'all'
        const allOption = select.querySelector('option[value="all"]')
        if (allOption) {
          select.value = 'all'
        } else {
          select.selectedIndex = 0
        }
      })
      
      // Desmarcar checkboxes
      form.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
        checkbox.checked = false
      })
      
      // Ocultar el wrapper de stock personalizado
      const stockCustomWrapper = document.getElementById('stock-custom-wrapper')
      if (stockCustomWrapper) {
        stockCustomWrapper.style.display = 'none'
      }
      
      // Actualizar badge de filtros
      this.updateFilterBadge()
      
      // Enviar el formulario para refrescar resultados
      form.requestSubmit()
    }
  }

  // Actualizar contador de caracteres para campos de notas
  updateNotesCounter(event) {
    const textarea = event.target
    const length = textarea.value.length
    
    // Determinar cuál contador actualizar basado en el textarea
    if (textarea.id === 'entries-notes' && this.hasEntriesNotesCounterTarget) {
      this.entriesNotesCounterTarget.textContent = length
    } else if (textarea.id === 'exits-notes' && this.hasExitsNotesCounterTarget) {
      this.exitsNotesCounterTarget.textContent = length
    }
  }

  // Obtener datos de los inventarios seleccionados desde la tabla
  getSelectedInventoryData() {
    const data = []
    
    this.checkboxTargets.forEach(checkbox => {
      if (this.selectedIds.has(checkbox.value)) {
        const row = checkbox.closest('tr')
        if (row) {
          // Usar data attributes de la fila
          const productCode = row.dataset.productCode || ''
          const size = row.dataset.inventorySize || ''
          const stock = parseInt(row.dataset.inventoryStock) || 0
          
          data.push({
            id: checkbox.value,
            product: productCode,
            size: size,
            quantity: stock
          })
        }
      }
    })
    
    return data
  }

  // Poblar tabla de entradas con productos seleccionados
  populateEntriesTable() {
    if (!this.hasEntriesTableContainerTarget) return
    
    const selectedData = this.getSelectedInventoryData()
    
    if (selectedData.length === 0) {
      this.entriesTableContainerTarget.innerHTML = `
        <p style="text-align: center; color: var(--text-muted); padding: 2rem;">
          No hay productos seleccionados. Marca los checkboxes en la tabla para agregar productos.
        </p>
      `
      if (this.hasSubmitEntriesButtonTarget) this.submitEntriesButtonTarget.disabled = true
      this.updateEntriesTotal()
      return
    }
    
    let tableHtml = `
      <table class="table" style="width: 100%;">
        <thead>
          <tr>
            <th>Producto</th>
            <th>Talla</th>
            <th>Stock Actual</th>
            <th style="width: 120px;">Cantidad a Ingresar</th>
          </tr>
        </thead>
        <tbody>
    `
    
    selectedData.forEach(item => {
      tableHtml += `
        <tr>
          <td>${item.product}</td>
          <td>${item.size}</td>
          <td>${item.quantity}</td>
          <td>
            <input type="number" 
                   class="form-control entry-input" 
                   data-inventory-id="${item.id}"
                   data-action="input->inventory-toolbar#updateEntriesTotal"
                   min="0" 
                   value="0"
                   style="width: 100%; text-align: center;">
          </td>
        </tr>
      `
    })
    
    tableHtml += '</tbody></table>'
    this.entriesTableContainerTarget.innerHTML = tableHtml
    
    if (this.hasSubmitEntriesButtonTarget) this.submitEntriesButtonTarget.disabled = false
    this.updateEntriesTotal()
  }

  // Poblar tabla de salidas con productos seleccionados
  populateExitsTable() {
    if (!this.hasExitsTableContainerTarget) return
    
    const selectedData = this.getSelectedInventoryData()
    
    if (selectedData.length === 0) {
      this.exitsTableContainerTarget.innerHTML = `
        <p style="text-align: center; color: var(--text-muted); padding: 2rem;">
          No hay productos seleccionados. Marca los checkboxes en la tabla para agregar productos.
        </p>
      `
      if (this.hasSubmitExitsButtonTarget) this.submitExitsButtonTarget.disabled = true
      this.updateExitsTotal()
      return
    }
    
    let tableHtml = `
      <table class="table" style="width: 100%;">
        <thead>
          <tr>
            <th>Producto</th>
            <th>Talla</th>
            <th>Stock Actual</th>
            <th style="width: 120px;">Cantidad a Retirar</th>
          </tr>
        </thead>
        <tbody>
    `
    
    selectedData.forEach(item => {
      tableHtml += `
        <tr>
          <td>${item.product}</td>
          <td>${item.size}</td>
          <td>${item.quantity}</td>
          <td>
            <input type="number" 
                   class="form-control exit-input" 
                   data-inventory-id="${item.id}"
                   data-max-quantity="${item.quantity}"
                   data-action="input->inventory-toolbar#updateExitsTotal"
                   min="0" 
                   max="${item.quantity}"
                   value="0"
                   style="width: 100%; text-align: center;">
          </td>
        </tr>
      `
    })
    
    tableHtml += '</tbody></table>'
    this.exitsTableContainerTarget.innerHTML = tableHtml
    
    if (this.hasSubmitExitsButtonTarget) this.submitExitsButtonTarget.disabled = false
    this.updateExitsTotal()
  }

  // Actualizar total de entradas
  updateEntriesTotal() {
    if (!this.hasEntriesTotalTarget) return
    
    let total = 0
    const inputs = this.element.querySelectorAll('.entry-input')
    
    inputs.forEach(input => {
      total += parseInt(input.value) || 0
    })
    
    this.entriesTotalTarget.textContent = `Total a ingresar: ${total} unidades`
    
    // Habilitar/deshabilitar botón según si hay cantidad
    if (this.hasSubmitEntriesButtonTarget) {
      this.submitEntriesButtonTarget.disabled = total === 0
    }
  }

  // Actualizar total de salidas
  updateExitsTotal() {
    if (!this.hasExitsTotalTarget) return
    
    let total = 0
    let hasErrors = false
    const inputs = this.element.querySelectorAll('.exit-input')
    
    inputs.forEach(input => {
      const value = parseInt(input.value) || 0
      const max = parseInt(input.dataset.maxQuantity) || 0
      
      if (value > max) {
        input.style.borderColor = 'var(--danger-color)'
        hasErrors = true
      } else {
        input.style.borderColor = ''
      }
      
      total += value
    })
    
    this.exitsTotalTarget.textContent = `Total a retirar: ${total} unidades`
    
    // Habilitar/deshabilitar botón según si hay cantidad y no hay errores
    if (this.hasSubmitExitsButtonTarget) {
      this.submitExitsButtonTarget.disabled = total === 0 || hasErrors
    }
  }

  // Manejar cambio en select de stock
  onStockChange(event) {
    const value = event.target.value
    const stockCustomWrapper = document.getElementById('stock-custom-wrapper')
    
    if (stockCustomWrapper) {
      stockCustomWrapper.style.display = value === 'custom' ? 'block' : 'none'
    }
    
    this.updateFilterBadge()
  }

  // Actualizar badge de filtros activos
  updateFilterBadge() {
    if (!this.hasFilterBadgeTarget) return
    
    let activeFilters = 0
    
    // Contar filtros activos
    const form = this.hasAdvancedPanelTarget 
      ? this.advancedPanelTarget.querySelector('form')
      : this.element.querySelector('.tool-panel form')
    
    if (form) {
      form.querySelectorAll('input[type="text"], input[type="number"]').forEach(input => {
        if (input.value && input.value.trim() !== '') {
          activeFilters++
        }
      })
      
      form.querySelectorAll('select').forEach(select => {
        if (select.value && select.value !== 'all' && select.value !== '') {
          activeFilters++
        }
      })
    }
    
    // Actualizar badge
    if (activeFilters > 0) {
      this.filterBadgeTarget.textContent = activeFilters
      this.filterBadgeTarget.style.display = 'inline-flex'
    } else {
      this.filterBadgeTarget.style.display = 'none'
    }
  }

  // Registrar entradas de stock
  submitEntries(event) {
    event.preventDefault()
    
    if (this.selectedIds.size === 0) {
      toast.warning('Por favor selecciona al menos un producto')
      return
    }
    
    // Obtener notas del campo de texto
    const notes = this.hasEntriesNotesTarget ? this.entriesNotesTarget.value.trim() : ''
    
    // Recopilar datos de entradas del panel
    const movements = []
    const inputs = this.element.querySelectorAll('.entry-input')
    
    inputs.forEach(input => {
      const inventoryId = input.dataset.inventoryId
      const quantity = parseInt(input.value) || 0
      
      if (quantity > 0 && this.selectedIds.has(inventoryId)) {
        movements.push({
          inventory_id: inventoryId,
          quantity: quantity,
          notes: notes || null
        })
      }
    })
    
    if (movements.length === 0) {
      toast.warning('Por favor ingresa cantidades válidas')
      return
    }
    
    // Deshabilitar botón mientras procesa
    if (this.hasSubmitEntriesButtonTarget) {
      this.submitEntriesButtonTarget.disabled = true
      this.submitEntriesButtonTarget.textContent = 'Procesando...'
    }
    
    // Enviar al backend
    fetch('/admin/inventarios/bulk_stock_entry', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ movements: movements })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        toast.success(data.message || 'Entradas registradas correctamente')
        window.location.reload()
      } else {
        toast.error('Error al registrar entradas: ' + (data.message || 'Error desconocido'))
        if (this.hasSubmitEntriesButtonTarget) {
          this.submitEntriesButtonTarget.disabled = false
          this.submitEntriesButtonTarget.textContent = 'Registrar Entradas'
        }
      }
    })
    .catch(error => {
      console.error('Error:', error)
      toast.error('Error al registrar entradas')
      if (this.hasSubmitEntriesButtonTarget) {
        this.submitEntriesButtonTarget.disabled = false
        this.submitEntriesButtonTarget.textContent = 'Registrar Entradas'
      }
    })
  }

  // Registrar salidas de stock
  submitExits(event) {
    event.preventDefault()
    
    if (this.selectedIds.size === 0) {
      toast.warning('Por favor selecciona al menos un producto')
      return
    }
    
    // Obtener notas del campo de texto
    const notes = this.hasExitsNotesTarget ? this.exitsNotesTarget.value.trim() : ''
    
    // Recopilar datos de salidas del panel
    const movements = []
    const inputs = this.element.querySelectorAll('.exit-input')
    
    inputs.forEach(input => {
      const inventoryId = input.dataset.inventoryId
      const quantity = parseInt(input.value) || 0
      
      if (quantity > 0 && this.selectedIds.has(inventoryId)) {
        movements.push({
          inventory_id: inventoryId,
          quantity: quantity,
          notes: notes || null
        })
      }
    })
    
    if (movements.length === 0) {
      toast.warning('Por favor ingresa cantidades válidas')
      return
    }
    
    // Deshabilitar botón mientras procesa
    if (this.hasSubmitExitsButtonTarget) {
      this.submitExitsButtonTarget.disabled = true
      this.submitExitsButtonTarget.textContent = 'Procesando...'
    }
    
    // Enviar al backend
    fetch('/admin/inventarios/bulk_stock_exit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ movements: movements })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        toast.success(data.message)
        window.location.reload()
      } else {
        const errorMessages = data.errors?.map(e => e.message).join('\n') || data.message || 'Error desconocido'
        toast.error('Errores al registrar salidas: ' + errorMessages)
        if (this.hasSubmitExitsButtonTarget) {
          this.submitExitsButtonTarget.disabled = false
          this.submitExitsButtonTarget.textContent = 'Registrar Salidas'
        }
      }
    })
    .catch(error => {
      console.error('Error:', error)
      toast.error('Error al registrar salidas')
      if (this.hasSubmitExitsButtonTarget) {
        this.submitExitsButtonTarget.disabled = false
        this.submitExitsButtonTarget.textContent = 'Registrar Salidas'
      }
    })
  }
}
